import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export function useQueryString() {
    return new URLSearchParams(useLocation().search);
}

/**
 * A hook that retrieves and parses a query parameter from the URL.
 *
 * @param name - The name of the query parameter to retrieve
 * @param defaultValue - Optional default value to return if the parameter is not present
 * @returns The value of the query parameter, or the default value if not found
 */
export function useQueryParam<T extends string | null>(
    name: string,
    defaultValue: T
): T;
export function useQueryParam(name: string): string | null;
export function useQueryParam<T extends string | null>(
    name: string,
    defaultValue?: T
): string | null | T {
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const value = params.get(name);

    return value !== null ? value : defaultValue ?? null;
}

export function useQueryParamMutation() {
    const {
        pathname,
        search,
        state: locationState,
    } = useLocation() as {
        pathname: string;
        search: string;
        state: unknown;
    };
    const navigate = useNavigate();
    const params = new URLSearchParams(search);

    return {
        set: (key: string, value: string) => {
            params.set(key, value);
            navigate(
                { pathname, search: params.toString() },
                { state: locationState }
            );
        },
        setMultiple: (data: { [key: string]: string }) => {
            Object.keys(data).forEach((item: string) => {
                params.set(item, data[item]);
            });
            navigate(
                { pathname, search: params.toString() },
                { state: locationState }
            );
        },
        delete: (key: string) => {
            params.delete(key);
            navigate(
                { pathname, search: params.toString() },
                { state: locationState }
            );
        },
        deleteMultiple: (keys: string[]) => {
            keys.forEach((key) => {
                params.delete(key);
            });
            navigate(
                { pathname, search: params.toString() },
                { state: locationState }
            );
        },
    };
}
